/**
 * Tenant-service API — typed client for the organization's OWN tenant record
 * (gateway prefix /api/v1/tenants → tenant-service, see
 * api-gateway/src/config.py's routing table).
 *
 * Read:  `GET /api/v1/tenants/{id}` — scoped server-side to self OR the
 *        `platform_superadmin` realm role (`_require_tenant_read_access` in
 *        tenant-service/src/api/routes.py); denial surfaces as 404, never 403,
 *        so a cross-tenant id can't be distinguished from a missing one.
 * Write: `PATCH /api/v1/tenants/{id}` — same self-or-platform_superadmin scope
 *        NOW applies (see the `update_tenant` fix alongside this client),
 *        stacked on top of the pre-existing owner/admin role gate
 *        (`require_platform_admin`). A non-owner/admin gets 403; a cross-tenant
 *        owner/admin gets 404.
 *
 * `status` is intentionally NOT exposed here — activate/suspend are dedicated
 * platform-ops actions (`POST /tenants/{id}/activate|suspend`), not something
 * an organization edits about itself. `plan` is included in the update payload
 * shape (the backend accepts it), but the Organization settings page only
 * DISPLAYS it — plan changes go through the dedicated Abonnement/billing flow
 * (`lib/api/subscription.ts`, `/settings/subscription`) so the two surfaces
 * never race each other over the same field.
 */

import { ApiError, api } from './client';
import { getUserManager } from '@/auth/oidc';

const BASE = '/api/v1/tenants';

export type TenantPlan = 'free' | 'starter' | 'professional' | 'enterprise';
export type TenantStatus = 'active' | 'suspended' | 'pending' | 'deleted';

/** Mirrors tenant-service's `QuotaConfig` (domain/models.py). */
export interface TenantQuota {
  max_users: number;
  max_posts_per_month: number;
  max_scheduled_posts: number;
  max_social_accounts: number;
  max_ai_generations_per_month: number;
  storage_limit_mb: number;
}

/** Mirrors tenant-service's `Tenant` response model. */
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  plan: TenantPlan;
  status: TenantStatus;
  quota: TenantQuota;
  created_at: string;
  updated_at: string;
}

/**
 * Update payload — mirrors `TenantUpdate` server-side. `quota`, when sent,
 * MUST be the full object (never a partial patch): the backend model applies
 * pydantic defaults to any field left out, which would silently RESET that
 * quota field instead of leaving it untouched. Callers should merge onto the
 * currently-loaded tenant's quota before sending (see
 * `OrganizationSettingsPage.tsx`).
 */
export interface TenantUpdateInput {
  name?: string;
  domain?: string | null;
  plan?: TenantPlan;
  quota?: TenantQuota;
}

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * The caller's own tenant id, mirroring the pattern used across the SPA
 * (`lib/api/publishing.ts`'s `getIdentity`, `ContentListPage`/`CalendarPage`'s
 * inline `profile.tenant_id` read): prefer the OIDC profile claim, falling
 * back to decoding it straight off the access token. Returns null only when
 * genuinely absent (signed out, or a token minted without a tenant claim).
 */
export async function getMyTenantId(): Promise<string | null> {
  const user = await getUserManager().getUser();
  if (!user) return null;
  const profile = user.profile as Record<string, unknown> | undefined;
  const fromProfile = typeof profile?.tenant_id === 'string' ? (profile.tenant_id as string) : null;
  if (fromProfile) return fromProfile;
  if (user.access_token) {
    const claims = decodeJwt(user.access_token);
    const fromToken = typeof claims?.tenant_id === 'string' ? (claims.tenant_id as string) : null;
    if (fromToken) return fromToken;
  }
  return null;
}

/** True for a 404 — tenant missing OR the caller may not access it (the
 *  backend deliberately conflates the two; see this file's header). */
export function isTenantNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** True for a 403 — the caller is authenticated and in-tenant but lacks the
 *  owner/admin role required to write. Kept distinct from 404 so the UI can
 *  show "reserved to admins" instead of "not found". */
export function isForbiddenTenantWrite(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

/** Read one tenant by id (self, or platform_superadmin — see routes.py). */
export function getTenant(tenantId: string): Promise<Tenant> {
  return api.get<Tenant>(`${BASE}/${encodeURIComponent(tenantId)}`);
}

/**
 * Update the organization's own tenant record (name/domain/plan/quota).
 * Requires an owner/admin realm role AND self-or-platform_superadmin scope
 * (both enforced server-side — see this file's header).
 */
export function updateTenant(tenantId: string, data: TenantUpdateInput): Promise<Tenant> {
  return api.patch<Tenant>(`${BASE}/${encodeURIComponent(tenantId)}`, data);
}
